import { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Navigation from '../sections/Navigation';
import FooterSection from '../sections/FooterSection';
import './ResearchPage.css';
import fundedByLogo from '../assets/research_dpkc_funded_by_nac_lv.jpeg';
import leoCentrsLogo from '../assets/LEO_centrs.png';

const projects = {
  'dpkc-pipeline': {
    en: {
      projectLabel: 'DPKC Research Project',
      projectNumber: 'Project No.',
      projectTitle: 'Autonomous Underwater Pipeline Tracking and Scanning System Using Multi-beam Sonar',
      supportProvider: 'Support Provider',
      supportProviderValue: 'CFLA',
      role: 'Role in Project',
      roleValue: 'Sole Implementer',
      projectStart: 'Project Start',
      projectStartValue: '01.02.2025',
      projectEnd: 'Project End',
      projectEndValue: '01.04.2026',
      projectNumberValue: '2.2.1.3.i.0/2/24/A/CFLA/001',
      postedDate: '26.01.2025',
      summary: 'Project Summary',
      summaryText: 'The goal of the project is to develop a low-readiness prototype for an underwater pipeline identification and tracking system that includes sensors and software. The system aims to enable underwater drones to autonomously identify underwater pipelines based on sonar data, track them, and collect data so that potential damage can be identified through data analysis.',
      additionalInfo: 'Additional Information',
      additionalText: 'The implementation activities of the "Autonomous underwater pipeline tracking and scanning system using multi-beam sonar" project are related to sonar data collection and data analysis algorithm development, while the ABC Software-led project focuses on software development, underwater acoustics, and data analysis, which are two technologically and work package-wise different things.',
      postedOn: 'Posted on',
      fundedBy: 'Funded by',
      progressTitle: 'Project Progress',
      showFundedLogo: true,
      progressEntries: [
        {
          date: '2025-04-30',
          month: 'Apr',
          year: '2025',
          title: 'Phase 1',
          text: 'First implementation phase completed: laboratory-level prototype development initiated and multi-beam sonar physical and software integration with underwater drone completed, preparing the foundation for further data acquisition and algorithm development.',
        },
        {
          date: '2025-07-31',
          month: 'Jul',
          year: '2025',
          title: 'Phase 2',
          text: 'In the second phase, manual data collection (operator-controlled drone) was implemented and processing of collected data was started to provide a database for the development of pipeline identification and tracking algorithm.',
        },
        {
          date: '2025-10-31',
          month: 'Oct',
          year: '2025',
          title: 'Phase 3',
          text: 'In the third phase, manual data collection and data processing continued, while simultaneously developing laboratory prototype software and testing with the created dataset.',
        },
        {
          date: '2026-01-31',
          month: 'Jan',
          year: '2026',
          title: 'Phase 4',
          text: 'In the fourth phase, laboratory-level prototype software development was completed, including the implementation of pipeline detection and tracking algorithms. The algorithms were integrated with the underwater drone controller and tested using the collected sonar dataset, demonstrating pipeline identification and tracking functionality.',
        },
        {
          date: '2026-04-30',
          month: 'Apr',
          year: '2026',
          title: 'Phase 5',
          text: 'In the fifth phase, autonomous underwater testing and system validation were completed, demonstrating reliable pipeline detection and tracking in real-world and operationally representative conditions. The complete system was integrated and refined, resulting in a low-readiness prototype capable of autonomous pipeline identification, tracking and scanning using multi-beam sonar data.',
        },
      ],
    },
    lv: {
      projectLabel: 'DPKC Pētījuma Projekts',
      projectNumber: 'Projekta Nr.',
      projectTitle: 'Autonoma zemūdens cauruļvadu izsekošanas un skenēšanas sistēma izmantojot daudzstaru sonāru',
      supportProvider: 'Atbalsta sniedzējs',
      supportProviderValue: 'CFLA',
      role: 'Loma projektā',
      roleValue: 'Vienīgais īstenotājs',
      projectStart: 'Projekta uzsākšana',
      projectStartValue: '01.02.2025',
      projectEnd: 'Projekta pabeigšana',
      projectEndValue: '01.04.2026',
      projectNumberValue: '2.2.1.3.i.0/2/24/A/CFLA/001',
      postedDate: '26.01.2025',
      summary: 'Projekta kopsavilkums',
      summaryText: 'Projekta mērķis ir izstrādāt zemas gatavības prototipu zemūdens cauruļvadu identificēšanas un izsekošanas sistēmai, kas iekļauj sensorus un programmatūru. Sistēmas mērķis ir ļaut zemūdens dronam autonomi identificēt zemūdens cauruļvadus balstoties uz sonāra datiem, spēt tiem izsekot un ievākt datus, lai datu analīzes rezultātā varētu noteikt potenciālus bojājumus.',
      additionalInfo: 'Papildu informācija',
      additionalText: '"Autonoma zemūdens cauruļvadu izsekošanas un skenēšanas sistēma izmantojot daudzstaru sonāru" projekta īstenošanas darbības ir saistītas ar sonāra datu ievākšanu un datu analīzes algoritmu izstrādi, savukārt ABC Software vadītajā projektā fokuss ir uz programmatūras izstrādi, zemūdens akustiku un datu analīzi, kas tehnoloģiski un darba pakotņu ziņā ir divas dažādas lietas.',
      postedOn: 'Publicēts',
      fundedBy: 'Finansē',
      progressTitle: 'Projekta progress',
      showFundedLogo: true,
      progressEntries: [
        {
          date: '2025-04-30',
          month: 'Apr',
          year: '2025',
          title: '1. posms',
          text: 'Noslēgts pirmais īstenošanas posms: uzsākta laboratorijas līmeņa prototipa izstrāde un veikta daudzstaru sonāra fiziskā un programmatūras integrācija ar zemūdens dronu, sagatavojot pamatu turpmākai datu ieguvei un algoritmu attīstībai.',
        },
        {
          date: '2025-07-31',
          month: 'Jūl',
          year: '2025',
          title: '2. posms',
          text: 'Otrajā posmā realizēta manuāla datu ievākšana (operatora vadīts drons) un uzsākta ievākto datu apstrāde, lai nodrošinātu datu bāzi cauruļvadu identificēšanas un izsekošanas algoritma izstrādei.',
        },
        {
          date: '2025-10-31',
          month: 'Okt',
          year: '2025',
          title: '3. posms',
          text: 'Trešajā posmā turpināta manuālā datu ievākšana un datu apstrāde, paralēli attīstot laboratorijas prototipa programmatūru un veicot testēšanu ar izveidoto datu kopu.',
        },
        {
          date: '2026-01-31',
          month: 'Jan',
          year: '2026',
          title: '4. posms',
          text: 'Ceturtajā posmā tika pabeigta laboratorijas līmeņa prototipa programmatūras izstrāde, tostarp cauruļvadu identificēšanas un izsekošanas algoritmu ieviešana. Algoritmi tika integrēti ar zemūdens drona vadības sistēmu un testēti, izmantojot ievākto sonāra datu kopu, demonstrējot cauruļvadu identificēšanas un izsekošanas funkcionalitāti.',
        },
        {
          date: '2026-04-30',
          month: 'Apr',
          year: '2026',
          title: '5. posms',
          text: 'Piektajā posmā tika pabeigti autonomās sistēmas testi ūdenī un sistēmas validācija, demonstrējot cauruļvadu identificēšanu un izsekošanu reālos un operacionālajai videi pietuvinātos apstākļos. Sistēmas komponentes tika integrētas un pilnveidotas, izstrādājot zemas gatavības līmeņa prototipu, kas spēj autonomi identificēt, izsekot un skenēt zemūdens cauruļvadus, izmantojot daudzstaru sonāra datus.',
        },
      ],
    },
  },

  'cfla-lars': {
    en: {
      projectLabel: 'CFLA Research Project',
      projectNumber: 'Project No.',
      projectTitle: 'Development of an Autonomous LARS System for USV–AUV Integration for Underwater Infrastructure Inspection',
      supportProvider: 'Support Provider',
      supportProviderValue: 'CFLA',
      role: 'Role in Project',
      roleValue: 'Partner – AUV platform provider, autonomy software developer, and USV–AUV system integration lead',
      projectStart: 'Project Start',
      projectStartValue: '01.04.2026',
      projectEnd: 'Project End',
      projectEndValue: '31.03.2027',
      projectNumberValue: '1.2.1.2.i.2/1/24/A/CFLA/007',
      postedDate: '07.04.2026',
      programme: 'Programme Framework',
      programmeText: 'SUBmerge Baltic is implementing a research project within the framework of the implementation regulations of Measure 1.2.1.2.i.2 "Development of Innovative Products and Technologies" under Investment 1.2.1.2.i "Improving Energy Efficiency in Business (including the transition to renewable energy technologies in heat supply and research and development activities, incl. in the bioeconomy)" of Reform and Investment Direction 1.2 "Improving Energy Efficiency" of Latvia\'s Recovery and Resilience Facility Plan.',
      summary: 'Project Summary',
      summaryText: 'Development of an autonomous LARS system enabling seamless USV–AUV integration for underwater infrastructure inspection. The project focuses on autonomous deployment, positioning, and recovery of AUVs in dynamic marine conditions, delivering high-resolution 3D data while reducing operational costs and reliance on crewed vessels.',
      additionalInfo: 'Additional Information',
      additionalText: 'The project builds on existing autonomous AUV technology and focuses on hardware, electronics, and system integration rather than new software development. It emphasises multi-modal communication (optical, acoustic, and radio) and precise positioning in challenging marine environments, with strong potential for commercialisation in offshore infrastructure monitoring markets.',
      postedOn: 'Posted on',
      fundedBy: 'Funded by',
      collaborationWith: 'In collaboration with',
      progressTitle: 'Project Progress',
      showFundedLogo: true,
      showCollaborationLogo: true,
      progressEntries: [
        {
          date: '2026-04-01',
          month: 'Apr',
          year: '2026',
          title: 'Project Start',
          text: 'The project officially commenced on 1 April 2026. This 12-month research and development initiative focuses on developing an autonomous LARS (Launch and Recovery System) for seamless USV–AUV integration, targeting autonomous deployment, positioning, and recovery of AUVs in dynamic marine conditions for underwater infrastructure inspection.',
        },
      ],
    },
    lv: {
      projectLabel: 'CFLA Pētījuma Projekts',
      projectNumber: 'Projekta Nr.',
      projectTitle: 'Autonomas LARS sistēmas izstrāde USV–AUV integrācijai zemūdens infrastruktūras apsekošanai',
      supportProvider: 'Atbalsta sniedzējs',
      supportProviderValue: 'CFLA',
      role: 'Loma projektā',
      roleValue: 'Partneris – AUV platformas nodrošinātājs, autonomijas programmatūras izstrādātājs un USV–AUV sistēmas integrācijas vadītājs',
      projectStart: 'Projekta uzsākšana',
      projectStartValue: '01.04.2026',
      projectEnd: 'Projekta pabeigšana',
      projectEndValue: '31.03.2027',
      projectNumberValue: '1.2.1.2.i.2/1/24/A/CFLA/007',
      postedDate: '07.04.2026',
      programme: 'Programmas ietvars',
      programmeText: 'SUBmerge Baltic īsteno pētniecības projektu Latvijas Atveseļošanas un noturības mehānisma plāna 1.2. reformu un investīciju virziena "Energoefektivitātes uzlabošana" 1.2.1.2.i. investīcijas "Energoefektivitātes paaugstināšana uzņēmējdarbībā (ietverot pāreju uz atjaunojamo energoresursu tehnoloģiju izmantošanu siltumapgādē un pētniecības un attīstības aktivitātes (t. sk. bioekonomikā))" 1.2.1.2.i.2. pasākuma "Inovatīvu produktu un tehnoloģiju izstrāde" īstenošanas noteikumu ietvaros.',
      summary: 'Projekta kopsavilkums',
      summaryText: 'Autonomas LARS sistēmas izstrāde, kas nodrošina efektīvu USV–AUV integrāciju zemūdens infrastruktūras apsekošanai. Projekts koncentrējas uz AUV autonomu palaišanu, pozicionēšanu un savākšanu dinamiskos jūras apstākļos, nodrošinot augstas izšķirtspējas 3D datu ieguvi, vienlaikus samazinot operacionālās izmaksas un nepieciešamību pēc apkalpotiem kuģiem.',
      additionalInfo: 'Papildu informācija',
      additionalText: 'Projekts balstās uz esošajām autonomo AUV tehnoloģijām un koncentrējas uz aparatūras, elektronikas un sistēmu integrāciju, nevis jaunas programmatūras izstrādi. Tajā tiek uzsvērta daudzkanālu komunikācija (optiskā, akustiskā un radio) un precīza pozicionēšana sarežģītos jūras apstākļos, ar augstu komercializācijas potenciālu atkrastes infrastruktūras monitoringa tirgos.',
      postedOn: 'Publicēts',
      fundedBy: 'Finansē',
      collaborationWith: 'Sadarbībā ar',
      progressTitle: 'Projekta progress',
      showFundedLogo: true,
      showCollaborationLogo: true,
      progressEntries: [
        {
          date: '2026-04-01',
          month: 'Apr',
          year: '2026',
          title: 'Projekta uzsākšana',
          text: 'Projekts oficiāli uzsākts 2026. gada 1. aprīlī. Šī 12 mēnešu pētniecības un izstrādes iniciatīva koncentrējas uz autonomas LARS (palaišanas un savākšanas sistēmas) izstrādi efektīvai USV–AUV integrācijai, mērķējot uz AUV autonomu palaišanu, pozicionēšanu un savākšanu dinamiskos jūras apstākļos zemūdens infrastruktūras apsekošanai.',
        },
      ],
    },
  },

  'cfla18-auv': {
    en: {
      projectLabel: 'CFLA Research Project',
      projectNumber: 'Project No.',
      projectTitle: 'Scalable Dual-Use Autonomous Underwater Drone System with Integrated Data Infrastructure and STANAG-Compliant Electromagnetic Profile',
      supportProvider: 'Funding Recipient',
      supportProviderValue: 'ABC Software Ltd.',
      role: 'Role in Project',
      roleValue: 'Project Partner',
      projectStart: 'Project Start',
      projectStartValue: '01.09.2025',
      projectEnd: 'Project End',
      projectEndValue: '31.08.2027',
      projectNumberValue: '1.2.1.1/3/25/A/018',
      postedDate: '01.09.2025',
      summary: 'Project Summary',
      summaryText: 'The goal of the project is to develop a prototype of an autonomous underwater drone (AUV) system — comprising the drone and its support software — that is modular and has dual-use capabilities: for civilian purposes (monitoring offshore oil and gas platforms, wind farms, and underwater infrastructure) and for military applications. The project will progress through Industrial Research (reaching TRL5) and Experimental Development phases (TRL7 and TRL8), resulting in a fully functional AUV prototype. Total project budget: 986,488 EUR (719,632 EUR public funding, 266,856 EUR private co-financing).',
      additionalInfo: 'Additional Information',
      additionalText: 'The project is a collaborative effort led by ABC Software, with SUBmerge Baltic as a key partner specializing in underwater drone design and development, and Tactical AI focusing on UAV electronics design and cyber-secure control platforms. The planned deliverables include a lightweight composite hull AUV, low-EMI electronics, a cyber-secure embedded control platform, an optimized battery system, an OTA update system, a mission data storage and analysis platform with visualization, and dashboards tailored for both civilian and military client groups.',
      postedOn: 'Posted on',
      fundedBy: 'Funded by',
      progressTitle: 'Project Progress',
      showFundedLogo: true,
      progressEntries: [
        {
          date: '2025-09-01',
          month: 'Sep',
          year: '2025',
          title: 'Project Start',
          text: 'The project officially commenced on 1 September 2025. This 24-month collaborative research and development initiative brings together ABC Software (lead partner), SUBmerge Baltic, and Tactical AI with the shared goal of developing a modular, dual-use autonomous underwater drone (AUV) system prototype. The project is structured across three main activities: Industrial Research targeting TRL5 (through September 2026), followed by two Experimental Development phases reaching TRL7 (through May 2027) and the final beta prototype at TRL8 by August 2027.',
        },
      ],
    },
    lv: {
      projectLabel: 'CFLA Pētījuma Projekts',
      projectNumber: 'Projekta Nr.',
      projectTitle: 'Mērogojama duāla lietojuma autonomā zemūdens drona sistēma ar integrētu datu infrastruktūru un STANAG atbilstošu elektromagnētisko profilu',
      supportProvider: 'Finansējuma saņēmējs',
      supportProviderValue: 'SIA "ABC software"',
      role: 'Loma projektā',
      roleValue: 'Projekta partneris',
      projectStart: 'Projekta uzsākšana',
      projectStartValue: '01.09.2025',
      projectEnd: 'Projekta pabeigšana',
      projectEndValue: '31.08.2027',
      projectNumberValue: '1.2.1.1/3/25/A/018',
      postedDate: '01.09.2025',
      summary: 'Projekta kopsavilkums',
      summaryText: 'Projekta mērķis ir izstrādāt autonoma zemūdens drona (AZD) sistēmas prototipu — ietverot dronu un tā atbalsta programmatūru — kas ir modulārs un ar divējādu lietojumu: civilā jomā (atkrastes naftas un gāzes platformu, vēja parku un zemūdens infrastruktūras monitorings) un militārajā jomā. Projekts tiks īstenots caur Rūpnieciskā pētījuma (sasniedzot TRL5) un Eksperimentālās izstrādes posmiem (TRL7 un TRL8), rezultātā iegūstot pilnībā funkcionējošu AZD prototipu. Projekta kopējais budžets: 986 488 EUR (719 632 EUR publiskais finansējums, 266 856 EUR privātais līdzfinansējums).',
      additionalInfo: 'Papildu informācija',
      additionalText: 'Projekts ir sadarbības rezultāts, kuru vada ABC Software, ar SUBmerge Baltic kā galveno partneri, kas specializējas zemūdens dronu projektēšanā un izstrādē, un Tactical AI, kas fokusējas uz UAV elektronikas projektēšanu un kiberdroša vadības platformu izstrādi. Plānotie rezultāti ietver vieglu kompozītmateriālu korpusu AZD, zemas EMI emisijas elektroniku, kiberdroša iegultās vadības platformu, optimizētu bateriju sistēmu, OTA atjauninājumu sistēmu, misiju datu glabāšanas un analīzes platformu ar vizualizāciju, kā arī informācijas paneļus civilām un militārām klientu grupām.',
      postedOn: 'Publicēts',
      fundedBy: 'Finansē',
      progressTitle: 'Projekta progress',
      showFundedLogo: true,
      progressEntries: [
        {
          date: '2025-09-01',
          month: 'Sep',
          year: '2025',
          title: 'Projekta uzsākšana',
          text: 'Projekts oficiāli uzsākts 2025. gada 1. septembrī. Šis 24 mēnešu sadarbības pētniecības un izstrādes projekts apvieno ABC Software (vadošais partneris), SUBmerge Baltic un Tactical AI ar kopīgu mērķi — izstrādāt modulāru, divējādas lietojamības autonoma zemūdens drona (AZD) sistēmas prototipu. Projekts ir strukturēts trīs galvenajās aktivitātēs: Rūpnieciskais pētījums līdz TRL5 (līdz 2026. gada septembrim), kam seko divas Eksperimentālās izstrādes fāzes — sasniegot TRL7 (līdz 2027. gada maijam) un fināla beta prototips TRL8 līmenī līdz 2027. gada augustam.',
        },
      ],
    },
  },
};

const ResearchProjectPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialLang = searchParams.get('lang') === 'lv' ? 'lv' : 'en';
  const [language, setLanguage] = useState(initialLang);

  const projectData = projects[projectId];

  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    return `${day}.${month}.${year}`;
  };

  if (!projectData) {
    return (
      <div className="research-page">
        <Navigation />
        <main className="research-main">
          <div className="container">
            <div className="research-header">
              <h1 className="research-title">Project Not Found</h1>
              <button type="button" className="back-btn" onClick={() => navigate('/research')}>
                ← Back to Research
              </button>
            </div>
          </div>
        </main>
        <FooterSection />
      </div>
    );
  }

  const t = projectData[language];

  return (
    <div className="research-page">
      <Navigation />

      <main className="research-main">
        <div className="container">
          {/* Language Toggle */}
          <div className="language-toggle">
            <button
              type="button"
              className={`lang-btn ${language === 'en' ? 'active' : ''}`}
              onClick={() => setLanguage('en')}
            >
              EN
            </button>
            <button
              type="button"
              className={`lang-btn ${language === 'lv' ? 'active' : ''}`}
              onClick={() => setLanguage('lv')}
            >
              LV
            </button>
          </div>

          {/* Back Button */}
          <div className="project-back-nav">
            <button type="button" className="back-btn" onClick={() => navigate('/research')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12,19 5,12 12,5"></polyline>
              </svg>
              {language === 'lv' ? 'Atpakaļ uz pētniecību' : 'Back to Research'}
            </button>
          </div>

          {/* Page Header */}
          <div className="research-header">
            <span className="section-label">{t.projectLabel}</span>
            <h1 className="research-title">{language === 'lv' ? 'Pētniecība un Attīstība' : 'Research & Development'}</h1>
            <p className="research-subtitle">{language === 'lv' ? 'ES finansēti pētniecības projekti, kas attīsta zemūdens autonomijas tehnoloģijas' : 'EU-funded research projects advancing underwater autonomy technology'}</p>
          </div>

          {/* Project Card */}
          <div className="project-card">
            {/* Logo Section */}
            {t.showFundedLogo && (
              <div className="project-logos">
                <span className="logos-label">{t.fundedBy}:</span>
                <div className="logos-row">
                  <img src={fundedByLogo} alt="Funded by EU Next Generation / NAP" className="funded-logo" />
                </div>
              </div>
            )}
            {t.showCollaborationLogo && (
              <div className="project-logos">
                <span className="logos-label">{t.collaborationWith}:</span>
                <div className="logos-row">
                  <img src={leoCentrsLogo} alt="LEO centrs" className="funded-logo" />
                </div>
              </div>
            )}

            {/* Project Number */}
            <div className="project-number">
              <span className="label">{t.projectNumber}</span>
              <span className="value">{t.projectNumberValue}</span>
            </div>

            {/* Project Title */}
            <h2 className="project-title">{t.projectTitle}</h2>

            {/* Project Details Grid */}
            <div className="project-details">
              <div className="detail-item">
                <span className="detail-label">{t.supportProvider}</span>
                <span className="detail-value">{t.supportProviderValue}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">{t.role}</span>
                <span className="detail-value">{t.roleValue}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">{t.projectStart}</span>
                <span className="detail-value">{t.projectStartValue}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">{t.projectEnd}</span>
                <span className="detail-value">{t.projectEndValue}</span>
              </div>
            </div>

            {/* Programme Framework */}
            {t.programmeText && (
              <div className="project-section">
                <h3>{t.programme}</h3>
                <p>{t.programmeText}</p>
              </div>
            )}

            {/* Project Summary */}
            <div className="project-section">
              <h3>{t.summary}</h3>
              <p>{t.summaryText}</p>
            </div>

            {/* Additional Information */}
            <div className="project-section">
              <h3>{t.additionalInfo}</h3>
              <p>{t.additionalText}</p>
            </div>

            {/* Posted Date */}
            <div className="project-posted">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              <span>{t.postedOn}: {t.postedDate}</span>
            </div>
          </div>

          {/* Progress Section */}
          <div className="progress-section">
            <h2 className="progress-title">{t.progressTitle}</h2>

            <div className="progress-timeline">
              {t.progressEntries.map((entry, index) => (
                <div className="progress-entry" key={index}>
                  <div className="progress-date">
                    <span className="month">{entry.month}</span>
                    <span className="year">{entry.year}</span>
                  </div>
                  <div className="progress-content">
                    <h4>{entry.title}</h4>
                    <p>{entry.text}</p>
                    <div className="progress-posted">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      <span>{t.postedOn}: {formatDate(entry.date)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      <FooterSection />
    </div>
  );
};

export default ResearchProjectPage;
